import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import {
  AirExchangeEligibilityQuoteResponseEnum,
  ExchangeScenarioEnum,
  FtcEnum,
  VoidScenarioEnum,
  type AirExchangeEligibilityQuoteSuccess,
  type Copy,
  type ExchangeScenario,
} from "@b2bportal/air-exchange-api";
import type { ChangeFlightContactAirlineScenario } from "@b2bportal/core-flights";
import { FlightExchangeModalState } from "@b2bportal/core-types";
import {
  ensureExhaustive,
  FLIGHTS_EXCHANGE_PATH,
  flightsExchangeApi,
  flightsExchangeV2Api,
  selfServeApi,
} from "@b2bportal/core-utilities";
import { EncodeBookingResponseEnum, Product } from "@b2bportal/ivr-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ClientName } from "@hopper-b2b/types";
import { getEnvVariables } from "@hopper-b2b/utilities";
import { noop } from "lodash-es";
import { setPolicy, setPrevItinerary } from "../../../exchange/store";
import { getAirports, getAirlines } from "../../store/selectors";

export const useChangeFlightEligibility = (
  isEnabled: boolean,
  itinerary: BookedFlightItineraryWithDepartureTime,
  isExchangeV2Enabled = false
) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18nContext();
  const [exchangePolicy, setExchangePolicy] =
    useState<AirExchangeEligibilityQuoteSuccess>();
  const airports = useSelector(getAirports);
  const airlines = useSelector(getAirlines);
  const [modalState, setModalState] = useState(
    FlightExchangeModalState.Loading
  );
  const [policyCopy, setPolicyCopy] = useState<Copy>();
  const [supportId, setSupportId] = useState("");

  const fetchExchangePolicy = useCallback(async () => {
    if (isEnabled) {
      setModalState(FlightExchangeModalState.Loading);

      if (isExchangeV2Enabled) {
        try {
          const { data: policyV2Res } =
            await flightsExchangeV2Api.apiV2SelfserveFlightExchangeEligibilityQuotePost(
              { reservationId: itinerary.bookedItinerary.id }
            );

          // v2 eligibility response has the same discriminator/shape as v1
          switch (policyV2Res.AirExchangeEligibilityQuoteResponse) {
            case AirExchangeEligibilityQuoteResponseEnum.AirExchangeEligibilityQuoteFailure:
              setModalState(FlightExchangeModalState.LoadFailed);
              break;
            case AirExchangeEligibilityQuoteResponseEnum.AirExchangeEligibilityQuoteSuccess: {
              // v2 quoteId is not used for eligibility (comes from PQ schedule instead)
              const policy =
                policyV2Res as unknown as AirExchangeEligibilityQuoteSuccess;
              setExchangePolicy(policy);
              handlePolicyScenario(policy);
              break;
            }
          }
        } catch (err) {
          console.error(err);
          setModalState(FlightExchangeModalState.LoadFailed);
        }
      } else {
        try {
          const { data: policyRes } =
            await flightsExchangeApi.apiV0SelfserveFlightExchangeEligibilityQuotePost(
              { reservationId: itinerary.bookedItinerary.id }
            );

          switch (policyRes.AirExchangeEligibilityQuoteResponse) {
            case AirExchangeEligibilityQuoteResponseEnum.AirExchangeEligibilityQuoteFailure:
              setModalState(FlightExchangeModalState.LoadFailed);
              break;
            case AirExchangeEligibilityQuoteResponseEnum.AirExchangeEligibilityQuoteSuccess:
              setExchangePolicy(policyRes);
              handlePolicyScenario(policyRes);
              break;
          }
        } catch (err) {
          console.error(err);
          setModalState(FlightExchangeModalState.LoadFailed);
        }
      }
    } else {
      setModalState(FlightExchangeModalState.ContactSupport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, isExchangeV2Enabled, itinerary]);

  const fetchSupportId = useCallback(async () => {
    try {
      const { data: supportIdRes } = await selfServeApi.apiV0IvrEncodePost({
        bookingId: itinerary.bookedItinerary.id,
        product: Product.Flight,
      });

      if (
        supportIdRes.EncodeBookingResponse === EncodeBookingResponseEnum.Success
      ) {
        setSupportId(supportIdRes.encodedCode);
      }
    } catch (err) {
      console.error(err);
    }
  }, [itinerary]);

  /**
   * @description Used for narrowing types to the scenarios that have `airlines` property
   * @param {ExchangeScenario} policy
   */
  const getContactAirlineScenario = (
    policy?: ExchangeScenario
  ): ChangeFlightContactAirlineScenario | undefined => {
    if (
      policy &&
      (policy.ExchangeScenario === ExchangeScenarioEnum.AirlineControl ||
        policy.ExchangeScenario === ExchangeScenarioEnum.ContactAirline ||
        policy.ExchangeScenario === ExchangeScenarioEnum.NonExchangeable)
    ) {
      return policy;
    }
  };

  function goToContactSupport() {
    setModalState(FlightExchangeModalState.ContactSupport);
  }

  /**
   * @description
   * @param {AirExchangeEligibilityQuoteSuccess} [newPolicy] Optional param in
   * case policy was fetched and not yet saved in state
   */
  function goToExchange(newPolicy?: AirExchangeEligibilityQuoteSuccess) {
    // Defensive: check if newPolicy is actually a policy object (has exchangeScenario)
    // to avoid accidentally using a React event if called from onClick
    const isValidPolicy = newPolicy && "exchangeScenario" in newPolicy;
    const policy = isValidPolicy ? newPolicy : exchangePolicy;

    if (policy) {
      dispatch(setPolicy(policy));
      dispatch(
        setPrevItinerary({
          itinerary,
          context: {
            airports,
            airlines,
          },
        })
      );
      history.push(FLIGHTS_EXCHANGE_PATH(itinerary.bookedItinerary.id));
    }
  }

  function handlePolicyScenario(policy: AirExchangeEligibilityQuoteSuccess) {
    const {
      exchangeScenario: eScen,
      voidScenario: { VoidScenario: vScen },
    } = policy;
    let nextModalState = FlightExchangeModalState.LoadFailed;
    const isNubank = getEnvVariables("clientName") === ClientName.NUBANK;
    const isRbc = getEnvVariables("clientName") === ClientName.RBC;

    if (
      vScen === VoidScenarioEnum.PreTicketVoidable ||
      vScen === VoidScenarioEnum.TicketedVoid
    ) {
      nextModalState = FlightExchangeModalState.TicketedVoidable;
    } else {
      switch (eScen.ExchangeScenario) {
        case ExchangeScenarioEnum.AirlineControl:
        case ExchangeScenarioEnum.ContactAirline:
          nextModalState =
            isNubank || isRbc
              ? FlightExchangeModalState.ContactSupport
              : FlightExchangeModalState.ContactAirline;
          break;
        case ExchangeScenarioEnum.NonExchangeable:
          nextModalState = isRbc
            ? FlightExchangeModalState.ContactSupport
            : FlightExchangeModalState.ContactAirline;
          break;
        case ExchangeScenarioEnum.BookingPending:
        case ExchangeScenarioEnum.Canceled:
        case ExchangeScenarioEnum.CancellationPending:
          nextModalState = FlightExchangeModalState.IneligibleForExchange;
          break;
        case ExchangeScenarioEnum.ContactCustomerService:
        case ExchangeScenarioEnum.ContainsRemarks:
        case ExchangeScenarioEnum.Departed:
        case ExchangeScenarioEnum.MultiProvider:
          nextModalState = FlightExchangeModalState.ContactSupport;
          break;
        case ExchangeScenarioEnum.Exchangeable:
          goToExchange(policy);
          return; // Early return to avoid setting modal state
        case ExchangeScenarioEnum.Ftc:
          if (eScen.Ftc === FtcEnum.FtcAvailable) {
            goToExchange(policy);
            return; // Early return to avoid setting modal state
          }
          break;
        default:
          ensureExhaustive(eScen);
      }
    }

    if ("exchangeCopy" in eScen) {
      setPolicyCopy(eScen.exchangeCopy);
    }

    setModalState(nextModalState);
  }

  useEffect(() => {
    fetchExchangePolicy();
    fetchSupportId();
  }, [fetchExchangePolicy, fetchSupportId]);

  return {
    context: { exchangePolicy, modalState, policyCopy, supportId },
    copy: {
      cancelFlightBtn: t(
        "core-flights.postbooking.exchangeEligibility.cancelFlightBtn"
      ),
      canceled: {
        body: t("core-flights.postbooking.exchangeEligibility.canceledBody"),
        title: t("core-flights.postbooking.exchangeEligibility.canceledTitle"),
      },
      closeBtn: t("close.button"),
      contactSupportBtn: t(
        "core-flights.postbooking.exchangeEligibility.contactSupportBtn"
      ),
      contactSupport: {
        body: t(
          "core-flights.postbooking.exchangeEligibility.contactSupportBody"
        ),
        title: t(
          "core-flights.postbooking.exchangeEligibility.contactSupportTitle"
        ),
      },
      continueToExchangeBtn: t(
        "core-flights.postbooking.exchangeEligibility.continueToExchangeBtn"
      ),
      loadingPolicyMsg: t(
        "core-flights.postbooking.exchangeEligibility.loadingPolicyMsg"
      ),
      loadFailed: {
        body: t("core-flights.postbooking.exchangeEligibility.loadFailedBody"),
        title: t(
          "core-flights.postbooking.exchangeEligibility.loadFailedTitle"
        ),
      },
      modalTitle: t("core-flights.postbooking.exchangeEligibility.modalTitle"),
      tryAgainBtn: t(
        "core-flights.postbooking.exchangeEligibility.tryAgainBtn"
      ),
      visitAirlineBtn: (airlineName: string) =>
        t("core-flights.postbooking.exchangeEligibility.visitAirlineBtn", {
          airlineName,
        }),
    },
    handlers: {
      fetchExchangePolicy,
      getContactAirlineScenario,
      goToContactSupport,
      goToExchange,
      handlePolicyScenario,
      openFlightCancelModal: noop,
    },
  };
};

export type IChangeFlightEligibilityData = ReturnType<
  typeof useChangeFlightEligibility
>;
